import { zip } from '@mapbox/shp-write';
import { TimeoutEnum } from './../../../../core/enum/timeout.enum';
import { AzureTranslatorService } from './../../../../core/services/azure-translator.service';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import {
  ProductFile,
  FileMetaData,
  PopulateFormData,
  FormUploadFileDialogData,
  ProductOption,
  FileBlobInfo,
  UploadResponse,
  CopyResponse,
  DeleteResponse,
  Categories,
  INFORMATIVOS_DOC_ID,
} from 'app/shared/interfaces/upload-file-support-page';
import { SupportPageService } from 'app/core/services/support-page.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, from, Observable } from 'rxjs';
import { concatMap, switchMap } from 'rxjs/operators';
import { forkJoinOrEmpty } from 'app/utils/rxjs.utils';

interface FileInput {
  id: number;
  file?: File;
  fileName?: string;
  selectedLanguages: string[];
}

const INITIAL_FILE_INPUT_ID = 2;
const UPLOAD_TARGET_PRODUCT = 'product';
/** Informativos have no version field in the UI — this fills the blob path segment normally occupied by it. */
const DEFAULT_FILE_VERSION_SEGMENT = 'v1';
@Component({
  standalone: false,
  selector: 'app-form-upload-file',
  templateUrl: './form-upload-file.component.html',
  styleUrls: ['./form-upload-file.component.scss'],
})
export class FormUploadFileComponent implements OnInit {
  fileInputs: FileInput[] = [{ id: 1, selectedLanguages: [] }];
  nextInputId = INITIAL_FILE_INPUT_ID;
  fileExtensionError = false;
  uploadFormFile: FormGroup;
  disableButton = true;
  fileName: string;
  fileSize: number = 1024;
  selectedLanguage = 'ptBR';
  allLanguagesFilled = false;
  fileUploaded: File | null = null;
  metadataInputFormUploadFile: FormGroup;

  selectedProductLanguages: string[] = [];

  readonly allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/msword', // .doc
    'application/vnd.ms-excel', // .xls
    'text/plain', // .txt
    'application/zip', // .zip
    'text/csv', // .csv
    'application/octet-stream', // Co.bin, .agpu e binários
    'application/x-binary',
    'application/macbinary',
    '',
  ];

  readonly allowedExtensions = [
    'pdf',
    'docx',
    'xlsx',
    'doc',
    'xls',
    'txt',
    'zip',
    'csv',
    'bin',
    'agpu', // Arquivos binários proprietários
  ];

  readonly maxFileSize = 1000 * this.fileSize * this.fileSize;

  products: ProductOption[] = [];
  fileCategories: { value: string; label: string }[] = [];

  productCategory: string[] = [];

  allLanguages = [
    {
      code: 'ptBR',
      shortCode: 'PT',
      name: 'Português',
      flag: '../../../../../assets/images/support-page/flags/PT.png',
    },
    {
      code: 'en',
      shortCode: 'EN',
      name: 'Inglês',
      flag: '../../../../../assets/images/support-page/flags/EN.png',
    },
    {
      code: 'es',
      shortCode: 'ES',
      name: 'Espanhol',
      flag: '../../../../../assets/images/support-page/flags/ES.png',
    },
    {
      code: 'it',
      shortCode: 'IT',
      name: 'Italiano',
      flag: '../../../../../assets/images/support-page/flags/IT.png',
    },
    {
      code: 'fr',
      shortCode: 'FR',
      name: 'Francês',
      flag: '../../../../../assets/images/support-page/flags/FR.png',
    },
    {
      code: 'de',
      shortCode: 'DE',
      name: 'Alemão',
      flag: '../../../../../assets/images/support-page/flags/DE.png',
    },
    {
      code: 'pl',
      shortCode: 'PL',
      name: 'Polonês',
      flag: '../../../../../assets/images/support-page/flags/PL.png',
    },
    {
      code: 'ru',
      shortCode: 'RU',
      name: 'Russo',
      flag: '../../../../../assets/images/support-page/flags/RU.png',
    },
    {
      code: 'nl',
      shortCode: 'NL',
      name: 'Holandês',
      flag: '../../../../../assets/images/support-page/flags/NL.png',
    },
    {
      code: 'ch',
      shortCode: 'CH',
      name: 'Chinês',
      flag: '../../../../../assets/images/support-page/flags/CH.png',
    },
    {
      code: 'tr',
      shortCode: 'TR',
      name: 'Turco',
      flag: '../../../../../assets/images/support-page/flags/TR.png',
    },
    {
      code: 'ro',
      shortCode: 'RO',
      name: 'Romeno',
      flag: '../../../../../assets/images/support-page/flags/RO.png',
    },
    {
      code: 'hu',
      shortCode: 'HU',
      name: 'Húngaro',
      flag: '../../../../../assets/images/support-page/flags/HU.png',
    },
    {
      code: 'fi',
      shortCode: 'FI',
      name: 'Finlandês',
      flag: '../../../../../assets/images/support-page/flags/FI.png',
    },
  ];

  languages = this.allLanguages.filter((lang) =>
    ['ptBR', 'en', 'es', 'it'].includes(lang.code),
  );
  productLanguages = this.allLanguages.map((lang) => ({
    code: lang.shortCode,
    name: lang.name,
    flag: lang.flag,
  }));
  isEditMode = false;

  readonly informativosDocId = INFORMATIVOS_DOC_ID;
  readonly uploadTargetProduct = UPLOAD_TARGET_PRODUCT;

  constructor(
    private readonly fb: FormBuilder,
    private readonly supportPageService: SupportPageService,
    private readonly translateService: TranslateService,
    private readonly toastr: ToastrService,
    private readonly dialog: MatDialog,
    private readonly azureTranslatorService: AzureTranslatorService,
    @Inject(MAT_DIALOG_DATA) public data: FormUploadFileDialogData,
  ) {
    this.uploadFormFile = this.fb.group({
      uploadTarget: [this.uploadTargetProduct],
      productId: new FormControl(
        {
          value: this.data?.fileData?.productId || this.data?.productId || '',
          disabled: this.isEditMode,
        },
        Validators.required,
      ),
      productCategory: [''],
      fileCategory: new FormControl(
        { value: this.data?.category || '', disabled: this.isEditMode },
        Validators.required,
      ),
      fileVersion: ['', Validators.required],
      fileMetadataByLanguage: this.fb.group({}),
      uploadDate: ['', Validators.required],
    });

    this.uploadFormFile
      .get('productId')
      ?.valueChanges.subscribe((selectedProduct) => {
        if (!selectedProduct) return;
        const product = this.products.find((p) => p.value === selectedProduct);
        if (product) {
          this.uploadFormFile.patchValue({
            productCategory: product.category,
          });
        }
      });

    this.uploadFormFile
      .get('uploadTarget')
      ?.valueChanges.subscribe((target) => this.onUploadTargetChange(target));

    this.initializeLanguageForms();
  }

  initializeLanguageForms() {
    const fileMetadataByLanguageGroup = this.uploadFormFile.get(
      'fileMetadataByLanguage',
    ) as FormGroup;
    this.languages.forEach((lang) => {
      fileMetadataByLanguageGroup.addControl(
        lang.code,
        this.fb.group({
          fileName: ['', Validators.required],
          description: ['', Validators.required],
        }),
      );
    });
    this.uploadFormFile.updateValueAndValidity();
  }

  setLanguage(langCode: string) {
    this.selectedLanguage = langCode;
  }

  /**
   * Switches between attaching a file to a product/category and sending it to the
   * flat, product-less "Informativos" list. The product/category selects are hidden
   * for the Informativos target (not disabled), so the values set here still flow
   * through `uploadFormFile.value` when the file is saved.
   */
  private onUploadTargetChange(target: string): void {
    const fileVersionControl = this.uploadFormFile.get('fileVersion');

    if (target === this.informativosDocId) {
      this.uploadFormFile.patchValue({
        productId: this.informativosDocId,
        productCategory: this.informativosDocId,
        fileCategory: this.informativosDocId,
        fileVersion: '',
      });
      fileVersionControl?.clearValidators();
    } else {
      this.uploadFormFile.patchValue({
        productId: '',
        productCategory: '',
        fileCategory: '',
      });
      fileVersionControl?.setValidators(Validators.required);
    }
    fileVersionControl?.updateValueAndValidity();
  }

  ngOnInit() {
    const lang = localStorage.getItem('iso.lang') || 'ptBR';

    this.supportPageService.getSupportFileById('file-categories').subscribe({
      next: (data: Categories) => {
        if (data && data.categories && Object.keys(data.categories).length > 0) {
          this.fileCategories = Object.entries(data.categories).map(
            ([key, labels]: [string, any]) => ({
              value: key,
              label: labels[lang] || labels['ptBR'] || key,
            }),
          );
        } else {
          this.setDefaultFileCategories();
        }
      },
      error: () => this.setDefaultFileCategories(),
    });

    this.supportPageService.getAllSupportFile().subscribe({
      next: (supportFiles: ProductFile[]) => {
        this.products = this.parseProducts(supportFiles);

        if (this.data?.mode === 'edit') {
          this.handleEditMode();
        } else {
          this.handleCreateMode();
        }
      },
      error: (error) => console.error(error),
    });
  }

  private setDefaultFileCategories(): void {
    this.fileCategories = [
      { value: 'softwares', label: 'Softwares' },
      { value: 'guides', label: 'Guias' },
      { value: 'manuals', label: 'Manuais' },
      { value: 'others', label: 'Outros' },
    ];
  }

  private parseProducts(supportFiles: ProductFile[]) {
    return supportFiles
      .filter(
        (file: ProductFile) => file.productName && file.id !== 'categories',
      )
      .map((file) => ({
        value: file.id,
        label: file.productName,
        category: file.productCategory,
      }));
  }

  private handleEditMode(): void {
    this.isEditMode = true;
    this.populateFormForEdit(this.data.fileData, this.data.category);
    this.disableCategoryAndProduct();

    if (this.data.productId === this.informativosDocId) {
      const fileVersionControl = this.uploadFormFile.get('fileVersion');
      fileVersionControl?.clearValidators();
      fileVersionControl?.updateValueAndValidity();
    }

    if (
      Array.isArray(this.data.fileInputs) &&
      this.data.fileInputs.length > 0
    ) {
      this.fileInputs = this.data.fileInputs.map((fi, idx) => ({
        id: Number(fi.id) || idx + 1,
        fileName: fi.fileName,
        selectedLanguages: fi.selectedLanguages,
      }));
      this.nextInputId = this.fileInputs.length + 1;
    } else if (
      Array.isArray(this.data.fileData?.files) &&
      this.data.fileData.files.length > 0
    ) {
      this.fileInputs = this.data.fileData.files.map((file, idx) => ({
        id: idx + 1,
        fileName: file.physicalFileName,
        selectedLanguages: file.fileLanguages,
      }));
      this.nextInputId = this.fileInputs.length + 1;
    } else {
      this.fileInputs = [];
      this.nextInputId = 1;
    }
  }

  private handleCreateMode(): void {
    const today = new Date();
    const dateFormatted = today.toISOString().split('T')[0];
    this.uploadFormFile.get('uploadDate')?.setValue(dateFormatted);

    this.fileInputs = [];
    this.nextInputId = 1;
  }

  addFileInput() {
    const currentId = this.nextInputId;
    this.nextInputId++;
    this.fileInputs.push({ id: currentId, selectedLanguages: [] });
  }

  onLanguageChangeForFile(fileIndex: number, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const fileInput = this.fileInputs[fileIndex];

    if (checkbox.checked) {
      fileInput.selectedLanguages.push(checkbox.value);
    } else {
      fileInput.selectedLanguages = fileInput.selectedLanguages.filter(
        (lang) => lang !== checkbox.value,
      );
    }
  }

  onFileSelectedFor(index: number, event: Event) {
    const input = event.target as HTMLInputElement;
    if (input?.files && input.files.length > 0) {
      const file = input.files[0];
      if (this.validateFile(file)) {
        this.fileInputs[index].file = file;
        this.fileInputs[index].fileName = file.name;
        this.disableButton = false;
      } else {
        this.fileInputs[index].file = undefined;
        this.fileInputs[index].fileName = '';
      }
    }
  }

  removeFileInput(index: number) {
    this.fileInputs.splice(index, 1);
  }

  private disableCategoryAndProduct(): void {
    Promise.resolve().then(() => {
      this.uploadFormFile.get('fileCategory')?.disable();
      this.uploadFormFile.get('productId')?.disable();
    });
  }

  checkLanguages() {
    this.allLanguagesFilled = this.languages.every((lang) => {
      const metadata = this.uploadFormFile
        .get('fileMetadataByLanguage')
        ?.get(lang.code)?.value;
      this.metadataInputFormUploadFile = metadata;
      return metadata?.fileName?.trim() && metadata?.description?.trim();
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files && input.files.length > 0) {
      const file = input.files[0];

      if (this.validateFile(file)) {
        this.fileUploaded = file;
        this.fileName = file.name;
        this.disableButton = false;
      } else {
        this.fileUploaded = null;
        this.disableButton = true;
      }
    }
  }

  validateFile(file: File): boolean {
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const isTypeValid =
      this.allowedTypes.includes(file.type) ||
      this.allowedExtensions.includes(fileExtension);
    const isSizeValid = file.size <= this.maxFileSize;

    if (!isTypeValid) {
      this.translateService
        .get('global.support-error-file-not-accepted')
        .subscribe((msg) => {
          alert(msg);
        });
    }

    if (!isSizeValid) {
      this.translateService
        .get('global.support-error-file-size')
        .subscribe((msg) => {
          alert(msg);
        });
    }

    return isTypeValid && isSizeValid;
  }

  private areFileExtensionsEqual(): boolean {
    const extensions = this.fileInputs
      .filter((input) => input.file)
      .map((input) => input.file!.name.split('.').pop()?.toLowerCase());

    return extensions.every((ext) => ext === extensions[0]);
  }

  updateNewFile() {
    if (
      this.uploadFormFile.invalid ||
      this.fileInputs.some((f) => !f.file || f.selectedLanguages.length === 0)
    ) {
      this.uploadFormFile.markAllAsTouched();
      this.handleErrorAlert('global.support-fill-in-all-required-fields');
      return;
    }

    if (!this.areFileExtensionsEqual()) {
      this.fileExtensionError = true;
      this.handleErrorAlert(
        'global.support-error-file-extension-must-be-equal',
      );
      return;
    } else {
      this.fileExtensionError = false;
    }

    const formValues = this.uploadFormFile.value;
    const productId = formValues.productId;
    const productCategory = formValues.productCategory;
    // Informativos have no version input — fall back to a fixed segment so the
    // blob path never contains an empty "//" gap.
    const fileVersion = formValues.fileVersion || DEFAULT_FILE_VERSION_SEGMENT;

    this.supportPageService
      .getSupportFileById(productId)
      .pipe(
        switchMap((existingData: ProductFile) => {
          const updatedData: ProductFile = { ...existingData };
          updatedData.id = productId;
          updatedData.productCategory =
            updatedData.productCategory || productCategory;

          updatedData.fileDataCategories = updatedData.fileDataCategories || {
            guides: [],
            manuals: [],
            others: [],
            softwares: [],
          };

          const category = formValues.fileCategory;
          if (!updatedData.fileDataCategories[category]) {
            updatedData.fileDataCategories[category] = [];
          }

          const allLanguages = Array.from(
            new Set(
              this.fileInputs.flatMap((input) => input.selectedLanguages),
            ),
          );

          const firstFile = this.fileInputs[0].file!;

          const newFileData: FileMetaData = {
            productId,
            version: fileVersion,
            fileDate: formValues.uploadDate,
            fileLanguages: allLanguages,
            ptBR: formValues.fileMetadataByLanguage.ptBR,
            en: formValues.fileMetadataByLanguage.en,
            es: formValues.fileMetadataByLanguage.es,
            it: formValues.fileMetadataByLanguage.it,
            files: this.fileInputs
              .filter((input) => input.file)
              .map((input) => ({
                fileLanguages: input.selectedLanguages,
                filePathBlobStorage: `${productCategory}/${productId}/${category}/${fileVersion}/${formValues.uploadDate}/${input.file.name}`,
                physicalFileName: input.file.name,
              })),
            fileName: firstFile.name,
            fileVersion: fileVersion,
            filePath: `${productCategory}/${productId}/${category}/${fileVersion}/${formValues.uploadDate}/${firstFile.name}`,
          };

          updatedData.fileDataCategories[category].push(newFileData);

          return this.supportPageService
            .getPresignedUrl(
              newFileData.files.map((f) => f.filePathBlobStorage),
            )
            .pipe(
              switchMap((presignedUrls) => {
                const uploadCalls = presignedUrls.map((url, i) => {
                  return this.supportPageService.uploadFile(
                    this.fileInputs[i].file!,
                    url,
                  );
                });

                return forkJoin(uploadCalls).pipe(
                  switchMap(() => {
                    // The "Informativos" document may not exist in Cosmos yet on its
                    // first upload — postSupportFile() creates it, or transparently
                    // falls back to an update server-side if it already exists.
                    // updateSupportFile() requires a pre-existing document, which
                    // normal products always have (created via product management).
                    if (productId === this.informativosDocId) {
                      return this.supportPageService.postSupportFile(
                        updatedData,
                      );
                    }
                    return this.supportPageService.updateSupportFile(
                      productId,
                      updatedData,
                    );
                  }),
                );
              }),
            );
        }),
      )
      .subscribe({
        next: () => {
          this.handleSuccessAlert('global.support-success-upload-file');
          this.dialog.closeAll();
        },
        error: (err) => {
          this.handleErrorAlert('global.support-error-upload-file');
          console.error(err);
        },
      });
  }

  populateFormForEdit(file: PopulateFormData, category: string): void {
    this.uploadFormFile.patchValue({
      productId: this.data.productId,
      productCategory: file.productCategory,
      fileCategory: category,
      fileVersion: file.version || file.fileVersion,
      uploadDate: file.date || file.fileDate,
    });
    if (this.isEditMode) {
      this.uploadFormFile.get('fileCategory')?.disable();
      this.uploadFormFile.get('productId')?.disable();
    } else {
      this.uploadFormFile.get('fileCategory')?.enable();
      this.uploadFormFile.get('productId')?.enable();
    }
    this.selectedProductLanguages = [...(file.fileLanguages || [])];

    for (const lang of ['ptBR', 'en', 'es', 'it']) {
      const metadataGroup = this.uploadFormFile
        .get('fileMetadataByLanguage')
        ?.get(lang);
      if (metadataGroup) {
        metadataGroup.patchValue({
          fileName:
            file.translations?.[lang]?.fileName ||
            file[lang]?.fileName ||
            file.fileName ||
            '',
          description:
            file.translations?.[lang]?.description ||
            file[lang]?.description ||
            file.fileDescription ||
            '',
        });
      }
    }

    this.fileName =
      this.extractPhysicalName(file.filePath || '') || file.fileName || '';

    this.disableButton = false;
  }

  private isDuplicate(
    fileData: FileMetaData[],
    newFileData: FileMetaData,
  ): boolean {
    return fileData.some(
      (file: FileMetaData) =>
        file.filePath === newFileData.fileName &&
        file.fileVersion === newFileData.fileVersion,
    );
  }

  editFile() {
    if (this.uploadFormFile.invalid) {
      this.uploadFormFile.markAllAsTouched();
      this.handleErrorAlert('global.support-fill-in-all-required-fields');
      return;
    }

    const formValues = this.uploadFormFile.value;
    const productId =
      this.uploadFormFile.get('productId')?.value || this.data.productId;
    const productCategory =
      formValues.productCategory || this.data.productCategory;
    const fileCategory = this.uploadFormFile.get('fileCategory')?.value;
    const uploadDate = formValues.uploadDate;
    // Informativos have no version input — fall back to a fixed segment so the
    // blob path never contains an empty "//" gap.
    const newFileVersion = formValues.fileVersion || DEFAULT_FILE_VERSION_SEGMENT;

    const oldPhysicalName =
      this.data.physicalFileName ||
      this.data.fileData?.fileName ||
      this.extractPhysicalName(this.data.fileData?.filePath);

    const oldVersion =
      this.data.physicalFileVersion ||
      this.data.fileData?.fileVersion ||
      this.data.fileData?.version;

    const oldDate =
      this.data.fileData?.fileDate ||
      this.data.fileData?.date ||
      formValues.uploadDate;

    const metadataByLanguage = {};
    for (const lang of ['ptBR', 'en', 'es', 'it']) {
      const d = formValues.fileMetadataByLanguage[lang];
      metadataByLanguage[lang] = {
        fileName: d.fileName,
        description: d.description,
      };
    }

    const filesMeta = this.fileInputs
      .filter((input) => input.file || input.fileName)
      .map((input) => {
        let fileName;
        if (input.file) {
          fileName = input.file.name;
        } else {
          fileName = input.fileName;
        }
        return {
          fileLanguages: input.selectedLanguages,
          filePathBlobStorage: `${productCategory}/${productId}/${fileCategory}/${newFileVersion}/${uploadDate}/${fileName}`,
          physicalFileName: fileName,
        };
      });

    const firstFileName =
      filesMeta[0]?.physicalFileName ||
      this.fileUploaded?.name ||
      this.data.physicalFileName;

    const newBlobPath = `${productCategory}/${productId}/${fileCategory}/${newFileVersion}/${uploadDate}/${firstFileName}`;

    const allLanguages = Array.from(
      new Set(this.fileInputs.flatMap((input) => input.selectedLanguages)),
    );

    const updatedFileData: FileMetaData = {
      productId,
      version: newFileVersion,
      fileName: firstFileName,
      filePath: newBlobPath,
      fileDate: uploadDate,
      fileVersion: newFileVersion,
      fileLanguages: allLanguages,
      ptBR: metadataByLanguage['ptBR'],
      en: metadataByLanguage['en'],
      es: metadataByLanguage['es'],
      it: metadataByLanguage['it'],
      files: filesMeta,
    };

    this.supportPageService.getSupportFileById(productId).subscribe(
      (data: ProductFile) => {
        const categoryArray = data.fileDataCategories[fileCategory] || [];
        const index = categoryArray.findIndex((f) => {
          const candidatePhysicalName = this.extractPhysicalName(
            f.filePath || f.fileName,
          );
          const candidateVersion = f.fileVersion || f.version;
          const candidateDate = f.fileDate || f.date;
          return (
            candidatePhysicalName === oldPhysicalName &&
            candidateVersion === oldVersion &&
            candidateDate === oldDate
          );
        });

        if (index === -1) {
          this.handleErrorAlert(
            'global.support-change-product-category-message',
          );
          return;
        }

        const oldFiles = (categoryArray[index]?.files || []).map((f) => ({
          ...f,
          fileVersion: oldVersion,
          fileDate: categoryArray[index].fileDate || oldDate,
        }));

        categoryArray[index] = updatedFileData;
        data.fileDataCategories[fileCategory] = categoryArray;

        const updatedFiles = filesMeta.map((meta, idx) => ({
          ...meta,
          file: this.fileInputs[idx]?.file,
          fileVersion: newFileVersion,
          fileDate: uploadDate,
        }));

        this.supportPageService.updateSupportFile(productId, data).subscribe(
          async () => {
            await this.updateBlobStorageMultiple(
              oldFiles,
              updatedFiles,
              () => {
                this.handleErrorAlert('global.support-error-upload-file');
              },
              () => {
                this.showSuccessAndClose();
              },
            );
          },
          (err) => {
            this.handlePermissionError(err);
          },
        );
      },
      () => this.handleErrorAlert('global.support-error-search-data'),
    );
  }

  private async updateBlobStorageMultiple(
    originalFiles: FileBlobInfo[],
    updatedFiles: (FileBlobInfo & { file?: File })[],
    rollbackCallback: () => void,
    onComplete: () => void,
  ) {
    const originalFilesMap = this.buildOriginalFilesMap(originalFiles);
    const uploadObservables: Observable<UploadResponse>[] = [];
    const copyObservables: Observable<CopyResponse>[] = [];

    for (const updatedFile of updatedFiles) {
      const oldFile = originalFilesMap[updatedFile.physicalFileName];
      if (updatedFile.file) {
        // A new File is attached, so its content must be uploaded regardless of
        // whether the blob path (name/version/date) changed from the previous entry.
        uploadObservables.push(this.getUploadObservable(updatedFile));
        continue;
      }
      if (this.isPathChanged(oldFile, updatedFile)) {
        copyObservables.push(
          this.supportPageService.copyBlob(
            oldFile.filePathBlobStorage,
            updatedFile.filePathBlobStorage,
          ),
        );
      }
    }

    const staleFiles = originalFiles.filter(
      (oldFile) =>
        oldFile.filePathBlobStorage &&
        !this.existsInUpdatedFiles(oldFile, updatedFiles),
    );

    const writeOperations: Observable<unknown>[] = [
      ...uploadObservables,
      ...copyObservables,
    ];

    // An old blob is often the source of a pending copy: deleting it before
    // the copy lands loses the file, so deletions are only built once every
    // write has finished. `forkJoinOrEmpty` also lets a metadata-only edit
    // reach onComplete().
    forkJoinOrEmpty(writeOperations)
      .pipe(
        concatMap(() =>
          forkJoinOrEmpty(
            staleFiles.map((oldFile) => this.getDeleteObservable(oldFile)),
          ),
        ),
      )
      .subscribe({
        next: () => {
          onComplete();
        },
        error: (err) => {
          rollbackCallback();
          this.handlePermissionError(err);
        },
      });
  }

  private buildOriginalFilesMap(
    originalFiles: FileBlobInfo[],
  ): Record<string, FileBlobInfo> {
    const map: Record<string, FileBlobInfo> = {};
    (originalFiles || []).forEach((f) => {
      map[f.physicalFileName] = f;
    });
    return map;
  }

  private isPathChanged(
    oldFile: FileBlobInfo | undefined,
    updatedFile: FileBlobInfo & { file?: File },
  ): boolean {
    return (
      !!oldFile &&
      oldFile.filePathBlobStorage !== updatedFile.filePathBlobStorage
    );
  }

  private getUploadObservable(
    updatedFile: FileBlobInfo & { file?: File },
  ): Observable<UploadResponse> {
    return this.supportPageService
      .getPresignedUrl([updatedFile.filePathBlobStorage])
      .pipe(
        switchMap((presignedUrl) =>
          this.supportPageService.uploadFile(
            updatedFile.file!,
            presignedUrl[0],
          ),
        ),
      );
  }

  /**
   * Deletes the replaced blob by its stored path, as-is.
   *
   * @remarks
   * Rebuilding the path from the file name and the product's current category
   * points elsewhere after a category change, possibly at the copy just made.
   */
  private getDeleteObservable(
    oldFile: FileBlobInfo,
  ): Observable<DeleteResponse> {
    return this.supportPageService.deleteSupportBlob(
      oldFile.filePathBlobStorage,
    );
  }

  private existsInUpdatedFiles(
    oldFile: FileBlobInfo,
    updatedFiles: (FileBlobInfo & { file?: File })[],
  ): boolean {
    return updatedFiles.some(
      (updated) => updated.filePathBlobStorage === oldFile.filePathBlobStorage,
    );
  }

  private deleteOldBlobOnly(
    originalPhysicalFileName: string,
    originalFileVersion: string,
    productId: string,
    productCategory: string,
    fileCategory: string,
    uploadDate: string,
  ) {
    this.supportPageService
      .deleteProductFileFromBlob(
        productId,
        productCategory,
        originalPhysicalFileName,
        fileCategory,
        originalFileVersion,
        uploadDate,
      )
      .subscribe(
        () => {
          this.showSuccessAndClose();
        },
        (err) => {
          console.error(err);
          this.showSuccessAndClose();
        },
      );
  }

  private rollbackCosmosUpdate(originalData: ProductFile, productId: string) {
    this.supportPageService
      .updateSupportFile(productId, originalData)
      .subscribe(
        () => console.warn('Rollback ok'),
        (err) => console.error(err),
      );
  }

  private showSuccessAndClose() {
    this.translateService
      .get('global.alert.send-file-upload-success')
      .subscribe((msg) =>
        this.toastr.success(msg, '', {
          positionClass: 'toast-top-full-width',
          disableTimeOut: false,
        }),
      );

    this.uploadFormFile.reset();

    setTimeout(() => {
      this.dialog.closeAll();
    }, TimeoutEnum.Short);
  }

  private extractPhysicalName(filePath: string): string {
    if (!filePath) return '';
    const cleanPath = filePath.split('?')[0];
    const parts = cleanPath.split('/');
    return parts[parts.length - 1];
  }

  onSubmit() {
    if (this.isEditMode) {
      this.editFile();
    } else {
      this.updateNewFile();
    }
  }

  private handleErrorAlert(translationKey: string) {
    this.translateService.get(translationKey).subscribe((msg) =>
      this.toastr.error(msg, '', {
        positionClass: 'toast-top-full-width',
        disableTimeOut: false,
        timeOut: TimeoutEnum.MediumLong,
      }),
    );
  }

  private handleSuccessAlert(translationKey: string) {
    this.translateService.get(translationKey).subscribe((msg) =>
      this.toastr.success(msg, '', {
        positionClass: 'toast-top-full-width',
        disableTimeOut: false,
        timeOut: 4500,
      }),
    );
  }

  private handlePermissionError(error) {
    this.translateService
      .get('global.validate.error-permission')
      .subscribe((permissionMsg: string) => {
        const errorMessage =
          error.error?.text === permissionMsg ? permissionMsg : 'error';
        this.handleErrorAlert(errorMessage);
      });
  }

  closeDialog(): void {
    const dialogRef = this.dialog.closeAll();
  }

  translateFields() {
    const currentLang = this.selectedLanguage;
    const name = this.uploadFormFile.get(
      `fileMetadataByLanguage.${currentLang}.fileName`,
    )?.value;
    const desc = this.uploadFormFile.get(
      `fileMetadataByLanguage.${currentLang}.description`,
    )?.value;

    ['en', 'es', 'it'].forEach((lang) => {
      if (lang !== currentLang) {
        this.azureTranslatorService.translate(name, lang).subscribe((res) => {
          const translatedName = res[0].translations[0].text;
          this.uploadFormFile
            .get(`fileMetadataByLanguage.${lang}.fileName`)
            ?.setValue(translatedName);
        });

        this.azureTranslatorService.translate(desc, lang).subscribe((res) => {
          const translatedDesc = res[0].translations[0].text;
          this.uploadFormFile
            .get(`fileMetadataByLanguage.${lang}.description`)
            ?.setValue(translatedDesc);
        });
      }
    });
  }
}
